import React, { useEffect, useState } from 'react';
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  Card,
  CardBody,
  useToast,
  Divider,
  Button
} from '@chakra-ui/react';
import { QuinielaForm } from '../components/quinielas/QuinielaForm';
import { Match } from '../types';
import { quinielaService } from '../services/quinielaService';
import { useAuth } from '../context/AuthContext';
import { Instructions } from '../components/quinielas/Instructions';
import { Link as RouterLink } from 'react-router-dom';
import { rankingService } from '../services/rankingService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';

export const HomePage: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamLogos, setTeamLogos] = useState<{ [teamName: string]: string }>({});
  const [userPredictions, setUserPredictions] = useState<
    { match_id: string; predicted_score_a: number; predicted_score_b: number }[]
  >([]);
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const data = await quinielaService.getActiveMatches();
        const allMatches = Object.values(data).flat();
        setMatches(allMatches);
      } catch (error) {
        console.error('Error fetching matches:', error);
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los partidos',
          status: 'error',
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatches();
  }, [toast]);

  useEffect(() => {
    const fetchLogos = async () => {
      const logos: { [teamName: string]: string } = {};
      for (const match of matches) {
        if (match.team_a && !logos[match.team_a]) {
          logos[match.team_a] = (await rankingService.getTeamLogo(match.team_a)) || '';
        }
        if (match.team_b && !logos[match.team_b]) {
          logos[match.team_b] = (await rankingService.getTeamLogo(match.team_b)) || '';
        }
      }
      setTeamLogos(logos);
    };

    if (matches.length > 0) {
      fetchLogos();
    }
  }, [matches]);

  useEffect(() => {
    const fetchUserPredictions = async () => {
      if (!user) return;
      const predictionsRef = collection(db, 'predictions');
      const q = query(predictionsRef, where('user_id', '==', user.uid));
      const snapshot = await getDocs(q);
      const predictions = snapshot.docs.map(doc => doc.data());
      setUserPredictions(predictions as any);
    };

    fetchUserPredictions();
  }, [user]);

  const handleSubmitPredictions = async (predictions: Record<string, { score_a: number; score_b: number }>) => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesión para guardar predicciones',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      await quinielaService.submitPredictions(user.uid, predictions);
      toast({
        title: 'Éxito',
        description: 'Tus predicciones han sido guardadas',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error submitting predictions:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron guardar tus predicciones',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={6} align="stretch">
        <Box textAlign="center">
          <Heading size="xl" mb={2}>Quinielas Liga MX</Heading>
          <Text fontSize="lg" color="gray.600">
            Clausura 2024
          </Text>
        </Box>

        <Card>
          <CardBody>
            <Instructions />
          </CardBody>
        </Card>

        <Divider />

        <Box>
          <Heading size="lg" mb={4}>Captura tu Quiniela</Heading>
          <QuinielaForm 
            matches={matches}
            onSubmit={handleSubmitPredictions}
            isLoading={isLoading}
            teamLogos={teamLogos}
            userPredictions={userPredictions}
          />
        </Box>

        <Button
          as={RouterLink}
          to="/rankings"
          colorScheme="blue"
          width="full"
          replace
        >
          Ver Tabla de Posiciones
        </Button>
      </VStack>
    </Container>
  );
}; 